import React, { useState, useEffect } from 'react';
import { CheckCircle, X, Sparkles } from 'lucide-react';

const SuccessAlert = ({
  isVisible,
  onClose,
  title = "Success!",
  message = "Operation completed successfully",
  autoClose = true,
  duration = 4000,
  type = "success"
}) => {
  const [isAnimating, setIsAnimating] = useState(false);

  useEffect(() => {
    if (isVisible) {
      setIsAnimating(true);

      if (autoClose) {
        const timer = setTimeout(() => {
          handleClose();
        }, duration);

        return () => clearTimeout(timer);
      }
    }
  }, [isVisible, autoClose, duration]);

  const handleClose = () => {
    setIsAnimating(false);
    setTimeout(() => {
      onClose();
    }, 300);
  };

  if (!isVisible) return null;

  const getTypeStyles = () => {
    switch (type) {
      case 'success':
        return {
          bg: 'bg-gradient-to-r from-green-400 to-emerald-500',
          icon: CheckCircle,
          iconColor: 'text-white',
          sparkleColor: 'text-green-200'
        };
      case 'info':
        return {
          bg: 'bg-gradient-to-r from-blue-400 to-cyan-500',
          icon: CheckCircle,
          iconColor: 'text-white',
          sparkleColor: 'text-blue-200'
        };
      case 'warning':
        return {
          bg: 'bg-gradient-to-r from-yellow-400 to-orange-500',
          icon: CheckCircle,
          iconColor: 'text-white',
          sparkleColor: 'text-yellow-200'
        };
      default:
        return {
          bg: 'bg-gradient-to-r from-green-400 to-emerald-500',
          icon: CheckCircle,
          iconColor: 'text-white',
          sparkleColor: 'text-green-200'
        };
    }
  };

  const typeStyles = getTypeStyles();
  const IconComponent = typeStyles.icon;

  return (
    <>
      {/* Backdrop */}
      <div
        className={`fixed inset-0 bg-black bg-opacity-30 backdrop-blur-sm z-50 transition-opacity duration-300 ${
          isAnimating ? 'opacity-100' : 'opacity-0'
        }`}
        onClick={handleClose}
      />

      {/* Alert Container */}
      <div className="fixed inset-0 z-50 flex items-center justify-center p-4 pointer-events-none">
        <div
          className={`relative max-w-md w-full pointer-events-auto transform transition-all duration-500 ease-out ${
            isAnimating
              ? 'scale-100 opacity-100 translate-y-0'
              : 'scale-95 opacity-0 translate-y-8'
          }`}
        >
          {/* Main Alert Card */}
          <div className={`relative ${typeStyles.bg} rounded-2xl shadow-2xl overflow-hidden`}>
            {/* Animated Background Pattern */}
            <div className="absolute inset-0 opacity-20">
              <div className="absolute top-0 left-0 w-32 h-32 bg-white rounded-full -translate-x-16 -translate-y-16 animate-pulse"></div>
              <div className="absolute bottom-0 right-0 w-24 h-24 bg-white rounded-full translate-x-12 translate-y-12 animate-pulse delay-1000"></div>
            </div>

            {/* Close Button */}
            <button
              onClick={handleClose}
              className="absolute top-4 right-4 p-2 text-white hover:bg-white hover:bg-opacity-20 rounded-full transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-white focus:ring-opacity-50"
              aria-label="Close alert"
            >
              <X className="w-5 h-5" />
            </button>

            {/* Content */}
            <div className="relative p-8 text-center">
              {/* Floating Sparkles */}
              <div className="absolute top-6 left-6">
                <Sparkles className={`w-4 h-4 ${typeStyles.sparkleColor} animate-bounce delay-300`} />
              </div>
              <div className="absolute top-8 right-8">
                <Sparkles className={`w-3 h-3 ${typeStyles.sparkleColor} animate-bounce delay-700`} />
              </div>
              <div className="absolute bottom-8 left-8">
                <Sparkles className={`w-3 h-3 ${typeStyles.sparkleColor} animate-bounce delay-500`} />
              </div>

              {/* Main Icon */}
              <div className="relative mb-6">
                <div className="w-20 h-20 mx-auto bg-white bg-opacity-20 rounded-full flex items-center justify-center backdrop-blur-sm">
                  <IconComponent className={`w-10 h-10 ${typeStyles.iconColor} animate-pulse`} />
                </div>

                {/* Ripple Effect */}
                <div className="absolute inset-0 w-20 h-20 mx-auto border-4 border-white border-opacity-30 rounded-full animate-ping"></div>
              </div>

              {/* Text Content */}
              <h3 className="text-2xl font-bold text-white mb-3 animate-fade-in-up">
                {title}
              </h3>
              <p className="text-white text-opacity-90 text-lg leading-relaxed animate-fade-in-up delay-200">
                {message}
              </p>

              {/* Progress Bar (if auto-close) */}
              {autoClose && (
                <div className="mt-6">
                  <div className="w-full bg-white bg-opacity-20 rounded-full h-1 overflow-hidden">
                    <div
                      className="h-full bg-white rounded-full transition-all ease-linear"
                      style={{
                        animation: `shrink ${duration}ms linear forwards`
                      }}
                    />
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Glow Effect */}
          <div className={`absolute inset-0 ${typeStyles.bg} rounded-2xl blur-xl opacity-50 -z-10 animate-pulse`}></div>
        </div>
      </div>

      {/* Custom CSS for animations */}
      <style jsx>{`
        @keyframes fade-in-up {
          from {
            opacity: 0;
            transform: translateY(20px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }

        @keyframes shrink {
          from {
            width: 100%;
          }
          to {
            width: 0%;
          }
        }

        .animate-fade-in-up {
          animation: fade-in-up 0.6s ease-out forwards;
        }

        .delay-200 {
          animation-delay: 0.2s;
        }

        .delay-300 {
          animation-delay: 0.3s;
        }

        .delay-500 {
          animation-delay: 0.5s;
        }

        .delay-700 {
          animation-delay: 0.7s;
        }

        .delay-1000 {
          animation-delay: 1s;
        }
      `}</style>
    </>
  );
};

export default SuccessAlert;
